import { Component } from '@theme/component';
import { getScrollContainer, getScrollEventTarget, getScrollTop, scrollTo, scrollContainerMediaQuery } from '@theme/scroll-container';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { isSaved, toggleSaved, migrateSaved, savedEvent } from '@theme/donewell-saved-items';

/** Shared, translated UI copy emitted by snippets/scripts.liquid. */
/** @param {string} key @param {Record<string, string | number>} [values] @returns {string} */
export function translate(key, values = {}) {
  let value = (typeof Theme !== 'undefined' && Theme.donewellTranslations?.[key]) || key;
  for (const [name, replacement] of Object.entries(values)) value = value.split(`{{ ${name} }}`).join(String(replacement)).split(`__${name.toUpperCase()}__`).join(String(replacement));
  return value;
}

/** Own every listener and delayed callback for one rendered component instance. */
export class DonewellComponent extends Component {
  controller = new AbortController();
  timers = new Set();
  frames = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
    super.disconnectedCallback();
  }

  initialize() {
    this.cleanup();
    this.controller = new AbortController();
    this.setup();
  }

  /** @template {HTMLElement} [T=HTMLElement] @param {string} selector @returns {T | null} */
  element(selector) {
    const element = this.querySelector(selector);
    return element instanceof HTMLElement ? /** @type {T} */ (element) : null;
  }

  /** @param {string} selector @returns {HTMLElement[]} */
  elements(selector) { return [...this.querySelectorAll(selector)].filter((element) => element instanceof HTMLElement); }

  setup() {}
  teardown() {}

  cleanup() {
    this.controller?.abort();
    for (const timer of this.timers) clearTimeout(timer);
    for (const frame of this.frames) cancelAnimationFrame(frame);
    this.timers.clear();
    this.frames.clear();
    this.teardown();
  }

  /**
   * @template {keyof (GlobalEventHandlersEventMap & WindowEventMap)} T
   * @param {EventTarget | null | undefined} target
   * @param {T} type
   * @param {(event: (GlobalEventHandlersEventMap & WindowEventMap)[T]) => void} listener
   * @param {AddEventListenerOptions} [options]
   */
  on(target, type, listener, options = {}) {
    target?.addEventListener(type, /** @type {EventListener} */ (listener), { ...options, signal: this.controller.signal });
  }

  /** @param {() => void} callback @param {number} delay */
  later(callback, delay) {
    const timer = setTimeout(() => { this.timers.delete(timer); if (this.isConnected) callback(); }, delay);
    this.timers.add(timer);
  }

  /** @param {() => void} callback */
  frame(callback) {
    const frame = requestAnimationFrame(() => { this.frames.delete(frame); if (this.isConnected) callback(); });
    this.frames.add(frame);
  }
}

export const motionBehavior = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';

/** Resolve native anchor navigation without assuming the window is the scroller. */
/** @param {Element | null} link @returns {HTMLElement | null} */
function anchorTarget(link) {
  if (!(link instanceof HTMLAnchorElement)) return null;
  const url = new URL(link.href, location.href);
  if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== location.pathname.replace(/\/$/, '') || !url.hash || url.hash === '#') return null;
  try { return document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch { return null; }
}

/** @param {HTMLElement} target @param {URL} [url] */
function revealAndScroll(target, url) {
  for (let node = /** @type {HTMLElement | null} */ (target); node instanceof HTMLElement; node = node.parentElement) {
    if (node instanceof HTMLDetailsElement) node.open = true;
  }
  const container = getScrollContainer();
  const offset = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--dw-sticky-header-height')) || 0;
  const containerTop = container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
  scrollTo({ top: Math.max(0, getScrollTop() + target.getBoundingClientRect().top - containerTop - offset - 18), behavior: motionBehavior() });
  if (url && location.hash !== url.hash) history.pushState(null, '', url.hash);
  if (!target.hasAttribute('tabindex')) {
    target.setAttribute('tabindex', '-1');
    target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
  }
  target.focus({ preventScroll: true });
}

/** Native details remains usable before the module loads and without JavaScript. */
class DonewellHeader extends DonewellComponent {
  /** @type {Map<HTMLElement, boolean>} */
  inertElements = new Map();
  /** @type {ResizeObserver | undefined} */
  observer;
  /** @type {AbortController | undefined} */
  scrollController;

  /** @type {HTMLElement | null} */ header = null;
  /** @type {HTMLDetailsElement | null} */ menu = null;
  /** @type {HTMLElement | null} */ trigger = null;
  /** @type {HTMLElement | null} */ drawer = null;

  setup() {
    this.header = this.element('[data-dw-header]');
    this.menu = /** @type {HTMLDetailsElement | null} */ (this.element('[data-dw-mobile-menu]'));
    this.trigger = this.element('[data-dw-menu-button]');
    this.drawer = this.element('[data-dw-drawer]');
    if (!this.header || !this.menu || !this.trigger || !this.drawer) return;
    const { header, menu, trigger, drawer } = this;
    this.observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--dw-sticky-header-height', `${header.classList.contains('dw-header--sticky') ? header.getBoundingClientRect().height : 0}px`);
    });
    this.observer.observe(this.header);
    const bindScroll = () => {
      this.scrollController?.abort();
      this.scrollController = new AbortController();
      const update = () => header.classList.toggle('is-scrolled', getScrollTop() > 12);
      getScrollEventTarget().addEventListener('scroll', update, { passive: true, signal: this.scrollController.signal });
      update();
    };
    bindScroll();
    this.on(scrollContainerMediaQuery, 'change', bindScroll);
    this.on(this.menu, 'toggle', () => this.syncMenu());
    this.syncMenu();
    this.on(window, 'resize', () => { if (innerWidth > 1050) this.closeMenu(false); });
    this.on(this, 'click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(link instanceof HTMLAnchorElement) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = anchorTarget(link);
      this.closeMenu(false);
      if (!target) return;
      event.preventDefault();
      revealAndScroll(target, new URL(link.href));
    });
    this.on(document, 'click', (event) => {
      if (event.target instanceof Node && !this.contains(event.target)) this.closeDropdowns();
    });
    this.on(this, 'toggle', (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.matches('.dw-header__dropdown') || !details.open) return;
      this.closeDropdowns(details);
    }, { capture: true });
    this.on(document, 'keydown', (event) => {
      if (event.key === 'Escape') {
        if (menu.open) { event.preventDefault(); this.closeMenu(); }
        this.closeDropdowns();
      }
      if (event.key !== 'Tab' || !menu.open) return;
      const items = [...menu.querySelectorAll('summary, a[href], button:not(:disabled), [tabindex="0"]')].filter((item) => item instanceof HTMLElement && item.getClientRects().length);
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); /** @type {HTMLElement | undefined} */ (last)?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); /** @type {HTMLElement | undefined} */ (first)?.focus(); }
    });
  }

  /** @param {HTMLDetailsElement} [except] */
  closeDropdowns(except) {
    for (const details of this.elements('.dw-header__dropdown[open]')) if (details instanceof HTMLDetailsElement && details !== except) details.open = false;
  }

  syncMenu() {
    if (!this.menu || !this.trigger || !this.drawer) return;
    const open = this.menu.open;
    this.trigger.setAttribute('aria-expanded', String(open));
    this.trigger.setAttribute('aria-label', translate(open ? 'close_menu' : 'open_menu'));
    if (open) {
      lockScroll(this.menu);
      for (let branch = /** @type {HTMLElement | null} */ (this.menu); branch && branch !== document.body; branch = branch.parentElement) {
        for (const sibling of branch.parentElement?.children ?? []) {
          if (sibling === branch || !(sibling instanceof HTMLElement) || sibling.matches('script, style, link')) continue;
          if (!this.inertElements.has(sibling)) this.inertElements.set(sibling, sibling.inert);
          sibling.inert = true;
        }
      }
      this.element('[data-dw-drawer] a[href], [data-dw-drawer] summary')?.focus();
    } else {
      unlockScroll(this.menu);
      for (const [element, inert] of this.inertElements) element.inert = inert;
      this.inertElements.clear();
    }
  }

  closeMenu(restoreFocus = true) {
    if (!(this.menu instanceof HTMLDetailsElement) || !this.menu.open) return;
    this.menu.open = false;
    this.syncMenu();
    if (restoreFocus) this.trigger?.focus();
  }

  teardown() {
    this.closeMenu(false);
    this.observer?.disconnect();
    if (this.header) document.documentElement.style.removeProperty('--dw-sticky-header-height');
    this.scrollController?.abort();
    for (const [element, inert] of this.inertElements) element.inert = inert;
    this.inertElements.clear();
  }
}

class DonewellServices extends DonewellComponent {
  setup() {
    const cards = this.elements('[data-dw-service-card]').filter((card) => card instanceof HTMLDetailsElement);
    /** @param {HTMLDetailsElement} card */
    const open = (card) => { for (const item of cards) item.open = item === card; };
    this.on(this, 'toggle', (event) => { if (event.target instanceof HTMLDetailsElement && cards.includes(event.target) && event.target.open) open(event.target); }, { capture: true });
    this.on(document, 'click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(link instanceof HTMLAnchorElement)) return;
      const target = anchorTarget(link);
      if (!(target instanceof HTMLDetailsElement) || !cards.includes(target)) return;
      event.preventDefault(); open(target); revealAndScroll(target, new URL(link.href));
    });
    const fromHash = () => {
      let target;
      try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch { return; }
      if (target instanceof HTMLDetailsElement && cards.includes(target)) { open(target); this.frame(() => revealAndScroll(target)); }
    };
    this.on(window, 'hashchange', fromHash);
    fromHash();
  }
}

class DonewellProcess extends DonewellComponent {
  setup() {
    const tabs = [...this.elements('[data-process-tab]')];
    const panels = [...this.elements('[data-process-panel]')];
    if (!tabs.length || tabs.length !== panels.length) return;
    /** @param {number} index @param {boolean} [focus] */
    const activate = (index, focus = false) => {
      tabs.forEach((tab, i) => {
        tab.classList.toggle('is-active', i === index);
        tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1;
      });
      panels.forEach((panel, i) => { panel.hidden = i !== index; panel.classList.toggle('is-active', i === index); });
      if (focus) tabs[index]?.focus();
    };
    tabs.forEach((tab, index) => {
      this.on(tab, 'click', () => activate(index));
      this.on(tab, 'keydown', (event) => {
        const rtl = getComputedStyle(this).direction === 'rtl';
        let next;
        if (event.key === 'ArrowRight') next = (index + (rtl ? -1 : 1) + tabs.length) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : -1) + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault(); activate(next, true);
      });
    });
    this.setAttribute('data-enhanced', '');
    activate(Math.max(0, tabs.findIndex((tab) => tab.classList.contains('is-active'))));
  }
  teardown() {
    this.removeAttribute('data-enhanced');
    for (const panel of this.elements('[data-process-panel]')) panel.hidden = false;
  }
}

class DonewellContact extends DonewellComponent {
  setup() {
    const form = this.element('form');
    if (!(form instanceof HTMLFormElement)) return;
    const key = `donewell-contact:${form.id}`;
    const fields = [...form.querySelectorAll('[data-contact-restore]')].filter((field) => field instanceof HTMLInputElement || field instanceof HTMLSelectElement);
    const success = this.element('[data-contact-success]');
    const errors = this.element('[data-contact-errors]');
    // Only non-message custom fields are retained, in this tab, for a short retry window.
    try {
      if (success) sessionStorage.removeItem(key);
      else if (errors) {
        const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (saved && Date.now() - saved.time < 15 * 60 * 1000) {
          for (const field of fields) {
            const value = saved.fields?.[field.dataset.contactRestore || ''];
            if (typeof value === 'string') field.value = value;
          }
        }
        sessionStorage.removeItem(key);
      }
    } catch { /* Forms remain native when session storage is unavailable. */ }
    this.on(form, 'submit', () => {
      try {
        sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), fields: Object.fromEntries(fields.map((field) => [field.dataset.contactRestore, field.value])) }));
      } catch { /* Do not prevent submission when storage is unavailable. */ }
    });
    const feedback = success || errors;
    if (feedback) this.frame(() => feedback.focus({ preventScroll: false }));
  }
}

/** Native details panels need no script; poll state still has an accessible state. */
class DonewellBreakdown extends DonewellComponent {
  setup() {
    const buttons = [...this.elements('[data-dwp-poll-option]')];
    for (const button of buttons) {
      button.setAttribute('aria-pressed', String(button.classList.contains('is-active')));
      this.on(button, 'click', () => {
        for (const option of buttons) {
          option.classList.toggle('is-active', option === button);
          option.setAttribute('aria-pressed', String(option === button));
        }
      });
    }
  }
}

class DonewellDetailNav extends DonewellComponent {
  setup() {
    const dropdowns = this.elements('[data-dwp-dropdown]').filter((item) => item instanceof HTMLDetailsElement);
    this.on(this, 'toggle', (event) => {
      if (!(event.target instanceof HTMLDetailsElement) || !event.target.open) return;
      for (const details of dropdowns) if (details !== event.target) details.open = false;
    }, { capture: true });
    this.on(document, 'click', (event) => { if (event.target instanceof Node && !this.contains(event.target)) for (const details of dropdowns) details.open = false; });
    this.on(this, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      for (const details of dropdowns) {
        if (!details.open) continue;
        details.open = false; details.querySelector('summary')?.focus();
      }
    });
  }
}

class DonewellDetailSearch extends DonewellComponent {
  setup() {
    const input = this.element('input[type="search"]');
    if (!(input instanceof HTMLInputElement)) return;
    this.on(document, 'keydown', (event) => {
      const active = document.activeElement;
      if (event.key === '/' && !(active instanceof HTMLElement && (active.matches('input, textarea, select') || active.isContentEditable))) {
        event.preventDefault(); input.focus();
      }
      if (event.key === 'Escape' && active === input) { input.value = ''; input.blur(); }
    });
  }
}

class DonewellDetailHeader extends DonewellComponent {
  setup() {
    const root = this.element('[data-dwp-detail-header]');
    if (!root) return;
    const key = root.dataset.dwpDetailId;
    const save = this.element('[data-dwp-detail-save]');
    const icon = this.element('[data-dwp-save-icon]');
    const label = this.element('[data-dwp-save-label]');
    const share = this.element('[data-dwp-detail-share]');
    const more = this.element('[data-dwp-detail-more]');
    const menu = this.element('[data-dwp-detail-menu]');
    const copy = this.element('[data-dwp-copy-link]');
    const status = this.element('[data-dwp-detail-status]');
    /** @param {string} text */
    const announce = (text) => { if (status) status.textContent = text; };
    const update = () => {
      const saved = isSaved(key);
      if (save) { save.classList.toggle('is-saved', saved); save.setAttribute('aria-pressed', String(saved)); }
      if (icon) icon.textContent = saved ? '♥' : '♡';
      if (label) label.textContent = saved ? translate('saved') : (root.dataset.saveText ?? '');
    };
    migrateSaved(key, [root.dataset.legacySaveId]);
    update();
    this.on(window, 'storage', update); document.addEventListener(savedEvent, update, { signal: this.controller.signal });
    this.on(save, 'click', () => { toggleSaved(key); update(); announce(isSaved(key) ? translate('saved') : translate('removed_save')); });
    const close = (focus = false) => { if (menu) menu.hidden = true; more?.setAttribute('aria-expanded', 'false'); if (focus) more?.focus(); };
    this.on(more, 'click', () => { if (!menu) return; menu.hidden = !menu.hidden; more?.setAttribute('aria-expanded', String(!menu.hidden)); });
    this.on(document, 'click', (event) => { if (event.target instanceof Node && !event.target.parentElement?.closest('[data-dwp-more-wrapper]')) close(); });
    this.on(this, 'keydown', (event) => { if (event.key === 'Escape' && menu && !menu.hidden) close(true); });
    const copyLink = async () => {
      try { await navigator.clipboard.writeText(location.href); if (this.isConnected) announce(translate('copied')); }
      catch { if (this.isConnected) announce(translate('copy_failed')); }
    };
    this.on(copy, 'click', async () => { await copyLink(); if (this.isConnected) close(true); });
    this.on(share, 'click', async () => {
      try {
        if (navigator.share) await navigator.share({ title: root.dataset.dwpDetailTitle || document.title, url: location.href });
        else await copyLink();
      } catch (error) { if (error.name !== 'AbortError' && this.isConnected) announce(translate('share_failed')); }
    });
  }
}

class DonewellEstimator extends DonewellComponent {
  setup() {
    const service = this.element('[data-estimator-service]');
    const extras = this.element('[data-estimator-extras]');
    const output = this.element('[data-estimator-total]');
    if (!(service instanceof HTMLSelectElement) || !(extras instanceof HTMLInputElement) || !(output instanceof HTMLOutputElement)) return;
    const update = () => {
      const extraCount = Math.max(0, Math.min(10, Math.floor(Number(extras.value) || 0)));
      const base = Math.max(0, Number(service.value) || 0);
      const total = base + extraCount * Math.max(0, Number(this.dataset.componentPrice) || 0);
      output.value = new Intl.NumberFormat(document.documentElement.lang || 'nl', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(total);
    };
    this.on(service, 'change', update); this.on(extras, 'input', update);
    this.on(extras, 'change', () => { extras.value = String(Math.max(0, Math.min(10, Math.floor(Number(extras.value) || 0)))); update(); });
    update();
  }
}

for (const [tag, implementation] of Object.entries({
  'donewell-estimator-component': DonewellEstimator,
  'donewell-header-component': DonewellHeader,
  'donewell-services-component': DonewellServices,
  'donewell-process-component': DonewellProcess,
  'donewell-contact-component': DonewellContact,
  'donewell-breakdown-component': DonewellBreakdown,
  'donewell-detail-nav-component': DonewellDetailNav,
  'donewell-detail-search-component': DonewellDetailSearch,
  'donewell-detail-header-component': DonewellDetailHeader,
})) if (!customElements.get(tag)) customElements.define(tag, implementation);
